/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { notification, Tooltip } from 'antd';
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
} from '@heroicons/react/24/outline';
import {
  HandThumbDownIcon as HandThumbDownSolidIcon,
  HandThumbUpIcon as HandThumbUpSolidIcon,
} from '@heroicons/react/24/solid';
import {
  submitAiInsightFeedback,
  normalizeUsage,
  pickUsageFromMessage,
  readCachedUsage,
  normalizeAskEvansConfidence,
} from '../../services/aiInsightsApi';
import SpacerLogAiUsageLine from './SpacerLogAiUsageLine';

/**
 * Answer footer — Lessons Learned ChatView MessageFeedback sister:
 * outline/solid Heroicons thumbs, soft tint when selected (no border box).
 */
const SpacerLogAiAnswerActions = ({
  markdown,
  usage,
  feedbackTargetId,
  initialRating = null,
  confidence = null,
  feature,
  question,
  projectId,
  ragSessionId,
}) => {
  const [rating, setRating] = useState(() => {
    if (initialRating === 'good' || initialRating === 1) return 'good';
    if (initialRating === 'bad' || initialRating === -1) return 'bad';
    return null;
  });
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  const usageNorm =
    normalizeUsage(usage)
    || pickUsageFromMessage({ usage, feedbackTargetId })
    || readCachedUsage(feedbackTargetId);

  const isGood = rating === 'good' || rating === 1;
  const isBad = rating === 'bad' || rating === -1;
  const locked = rating != null;
  const confidenceKey = normalizeAskEvansConfidence(confidence);

  const handleFeedback = async (next) => {
    if (!feedbackTargetId || feedbackBusy || locked) return;
    setFeedbackBusy(true);
    setRating(next);
    try {
      await submitAiInsightFeedback({
        feedbackTargetId,
        rating: next,
        feature,
        question,
        markdown,
        projectId,
        ragSessionId,
      });
    } catch (err) {
      setRating(null);
      notification.error({
        message: 'Could not send feedback',
        description: String(err?.message || err),
      });
    } finally {
      setFeedbackBusy(false);
    }
  };

  if (!markdown && !feedbackTargetId && !usageNorm) return null;

  const confidenceDisplay = confidenceKey
    ? confidenceKey
    : '—';

  return (
    <div className="spacer-ask-evans__answer-footer">
      <div className="spacer-ask-evans__answer-actions">
        <div className="spacer-ask-evans__answer-row">
          <div className="spacer-ask-evans__answer-row-left">
            <div className="spacer-ask-evans__confidence-row">
              <span className="spacer-ask-evans__confidence-label">Confidence:</span>
              <Tooltip
                title={
                  confidenceKey
                    ? `Model confidence: ${confidenceKey}`
                    : 'Confidence was not recorded for this answer (e.g. older messages before server update).'
                }
              >
                <span
                  className={`spacer-ask-evans__confidence${
                    confidenceKey
                      ? ` spacer-ask-evans__confidence--${confidenceKey}`
                      : ' spacer-ask-evans__confidence--unknown'
                  }`}
                >
                  {confidenceDisplay}
                </span>
              </Tooltip>
            </div>

            {feedbackTargetId ? (
              <div
                className={`spacer-ask-evans__helpful${
                  locked ? ' spacer-ask-evans__helpful--locked' : ''
                }`}
                role="group"
                aria-label="Feedback"
              >
                <span className="spacer-ask-evans__helpful-label">Feedback</span>
                <Tooltip title={isGood ? 'Marked helpful' : 'Helpful'}>
                  <button
                    type="button"
                    className={`spacer-ask-evans__thumb${isGood ? ' is-good' : ''}`}
                    disabled={feedbackBusy || locked}
                    onClick={() => handleFeedback('good')}
                    aria-label="Thumbs up"
                    aria-pressed={isGood}
                  >
                    {isGood ? (
                      <HandThumbUpSolidIcon className="spacer-ask-evans__thumb-icon" aria-hidden />
                    ) : (
                      <HandThumbUpIcon className="spacer-ask-evans__thumb-icon" aria-hidden />
                    )}
                  </button>
                </Tooltip>
                <Tooltip title={isBad ? 'Marked not helpful' : 'Not helpful'}>
                  <button
                    type="button"
                    className={`spacer-ask-evans__thumb${isBad ? ' is-bad' : ''}`}
                    disabled={feedbackBusy || locked}
                    onClick={() => handleFeedback('bad')}
                    aria-label="Thumbs down"
                    aria-pressed={isBad}
                  >
                    {isBad ? (
                      <HandThumbDownSolidIcon className="spacer-ask-evans__thumb-icon" aria-hidden />
                    ) : (
                      <HandThumbDownIcon className="spacer-ask-evans__thumb-icon" aria-hidden />
                    )}
                  </button>
                </Tooltip>
              </div>
            ) : null}

            {usageNorm ? <SpacerLogAiUsageLine usage={usageNorm} inline /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpacerLogAiAnswerActions;
